import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Share2,
  Lock,
  ArrowLeft,
  AlertCircle,
  Sparkles,
  Copy,
  Droplet,
  Beaker,
  TestTube,
  Dna,
  HeartPulse,
  ScanLine,
  Activity,
  ShieldAlert,
  Info,
  DollarSign,
  Printer,
  Loader2,
  Mail,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import LoadingReport from "@/components/LoadingReport";
import { getReport, startCheckout, verifyCheckout, printReportUrl } from "@/lib/api";
import { CATEGORY_META, FILTER_TABS, groupTestsByCategory, groupOf } from "@/lib/categories";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const ICON_MAP = {
  Droplet, Beaker, TestTube, Dna, HeartPulse, ScanLine, Activity,
};

const PRIORITY_STYLES = {
  high:   { label: "HIGH",   badge: "bg-brand-blue text-white",                     dot: "bg-brand-blue" },
  medium: { label: "MEDIUM", badge: "bg-brand-green-light text-brand-green-hover",  dot: "bg-brand-green" },
  low:    { label: "LOW",    badge: "bg-slate-100 text-ink-600",                    dot: "bg-slate-400" },
};

function priorityStyles(p) {
  return PRIORITY_STYLES[p] || PRIORITY_STYLES.low;
}

const ACCESS_TIER_LABEL = {
  conventional: "Conventional",
  specialty: "Specialty",
  advanced: "Advanced",
};

function CategoryIcon({ category, className }) {
  const meta = CATEGORY_META[category];
  const Icon = ICON_MAP[meta?.icon] || Activity;
  return <Icon className={className} strokeWidth={2.2} />;
}

function TestCard({ test, blurBody, index, isImaging }) {
  const s = priorityStyles(test.priority);
  const catMeta = CATEGORY_META[test.category];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: Math.min(index, 4) * 0.05 }}
      data-testid={`test-card-${index}`}
      data-category={test.category}
      className={`rounded-2xl bg-white p-6 shadow-sm border ${
        isImaging
          ? "border-amber-300 ring-1 ring-amber-100"
          : "border-slate-100"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border ${catMeta?.chip || "bg-slate-100 text-slate-700 border-slate-200"}`}
            >
              <CategoryIcon category={test.category} className="h-3 w-3" />
              {catMeta?.label || test.category}
            </span>
            {test.access_tier && (
              <span className="text-[10px] font-semibold uppercase tracking-wider rounded-full bg-slate-50 text-ink-500 border border-slate-100 px-2 py-0.5">
                {ACCESS_TIER_LABEL[test.access_tier] || test.access_tier}
              </span>
            )}
          </div>
          <h3
            data-testid={`test-name-${index}`}
            className="mt-2 font-heading text-lg font-semibold text-ink-900 leading-snug"
          >
            {test.name}
          </h3>
          {test.sample_or_modality && (
            <p className="mt-1 text-xs text-ink-500">{test.sample_or_modality}</p>
          )}
        </div>
        <span
          className={`text-[10px] font-bold tracking-widest rounded-full px-2 py-1 shrink-0 ${s.badge}`}
        >
          {s.label}
        </span>
      </div>

      {isImaging && (test.red_flags_triggered?.length ?? 0) > 0 && (
        <div
          data-testid={`test-red-flags-${index}`}
          className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-4"
          style={blurBody ? { filter: "blur(6px)" } : undefined}
          aria-hidden={blurBody || undefined}
        >
          <div className="flex items-start gap-2">
            <ShieldAlert className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" strokeWidth={2.2} />
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-widest text-amber-800">
                Why this is justified
              </p>
              <ul className="mt-1 space-y-0.5 text-sm text-amber-900 leading-relaxed">
                {test.red_flags_triggered.map((rf, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <span className="mt-1.5 h-1 w-1 rounded-full bg-amber-700 shrink-0" />
                    {rf}
                  </li>
                ))}
              </ul>
              {/^(ct|computed tomography)/i.test(test.name) && (
                <p className="mt-2 text-[11px] text-amber-800 italic">
                  Note: CT involves ionizing radiation. Discuss the risk/benefit with your practitioner.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div
        className={`mt-4 space-y-3 ${blurBody ? "select-none pointer-events-none" : ""}`}
        style={blurBody ? { filter: "blur(6px)" } : undefined}
        aria-hidden={blurBody || undefined}
      >
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-widest text-brand-blue">Why this test</p>
          <p className="mt-1 text-sm text-ink-700 leading-relaxed">{test.why}</p>
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-widest text-brand-green-hover">
            What it can reveal
          </p>
          <p className="mt-1 text-sm text-ink-700 leading-relaxed">{test.what_it_reveals}</p>
        </div>
        {test.estimated_cost_range_usd && (
          <div className="flex items-center gap-1.5 text-xs text-ink-500 pt-1">
            <DollarSign className="h-3.5 w-3.5" strokeWidth={2.4} />
            <span>Estimated cost: {test.estimated_cost_range_usd}</span>
          </div>
        )}
      </div>
    </motion.div>
  );
}

function CategoryGroup({ group, blurAll, firstTestIndex, allTests }) {
  const label = group.label;
  return (
    <section
      data-testid={`category-group-${group.category}`}
      className="mt-8 first:mt-0"
    >
      <div className="flex items-center gap-2 mb-4">
        <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg border ${group.chip}`}>
          <CategoryIcon category={group.category} className="h-4 w-4" />
        </span>
        <h3 className="font-heading text-lg font-semibold text-ink-900">
          {label}{" "}
          <span className="text-ink-500 font-normal text-sm">
            ({group.tests.length})
          </span>
        </h3>
      </div>
      <div className="space-y-4">
        {group.tests.map((t) => {
          const absoluteIndex = allTests.indexOf(t);
          const isFirst = absoluteIndex === firstTestIndex;
          const isImaging = t.category === "imaging";
          // First-in-list preview: body blurred but header visible.
          // Rest: full blur handled at container level; here render normally
          // (the container above applies the visual blur).
          const blurBody = blurAll && isFirst;
          return (
            <TestCard
              key={`${t.name}-${absoluteIndex}`}
              test={t}
              index={absoluteIndex}
              blurBody={blurBody}
              isImaging={isImaging}
            />
          );
        })}
      </div>
    </section>
  );
}

export default function Report() {
  const { reportId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const forceUnlocked = searchParams.get("preview") === "unlocked";
  const checkoutStatus = searchParams.get("checkout");
  const sessionId = searchParams.get("session_id");

  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  // Email prompt modal
  const [modalPlan, setModalPlan] = useState(null); // "one_off" | "subscription" | null
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [starting, setStarting] = useState(false);

  // Post-checkout polling
  const [polling, setPolling] = useState(false);
  const [pollTimedOut, setPollTimedOut] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getReport(reportId);
        if (!cancelled) setReport(data);
      } catch (e) {
        if (!cancelled) {
          const status = e?.response?.status;
          if (status === 404) setError("Report not found.");
          else if (status === 410) setError("This report was generated with an older schema. Please generate a new one.");
          else setError("Couldn't load report.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  const unlocked = useMemo(
    () => forceUnlocked || Boolean(report?.is_unlocked),
    [forceUnlocked, report]
  );

  function reportUrl() {
    return `${window.location.origin}/r/${reportId}`;
  }

  async function copyToClipboard(text) {
    if (typeof navigator !== "undefined" && navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (_) { /* fall through */ }
    }
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.top = "0";
      ta.style.left = "0";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      ta.setSelectionRange(0, text.length);
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch (_) {
      return false;
    }
  }

  async function onShare() {
    const url = reportUrl();
    const ok = await copyToClipboard(url);
    if (ok) toast.success("Link copied — share it with anyone.");
    else toast.error("Could not copy — use the URL box to select and copy manually.");
  }

  function onUnlockCta(kind) {
    // kind = "onetime" | "subscription" — normalize to plan value
    const plan = kind === "onetime" ? "one_off" : "subscription";
    setModalPlan(plan);
    setEmail("");
    setEmailError("");
  }

  function onPrint() {
    window.open(printReportUrl(reportId), "_blank", "noopener,noreferrer");
  }

  async function onSubmitCheckout(e) {
    e.preventDefault();
    const trimmed = email.trim();
    // Simple email format check.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setEmailError("");
    setStarting(true);
    try {
      const { checkout_url } = await startCheckout({
        reportId,
        plan: modalPlan,
        email: trimmed,
      });
      if (!checkout_url) {
        toast.error("This workup is already unlocked.");
        setModalPlan(null);
        setStarting(false);
        return;
      }
      window.location.assign(checkout_url);
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || "Could not start checkout. Please try again.";
      toast.error(msg);
      setStarting(false);
    }
  }

  // Handle checkout callbacks (?checkout=success&session_id=..  or  ?checkout=cancelled)
  useEffect(() => {
    if (checkoutStatus === "cancelled") {
      toast("Checkout cancelled", {
        description: "Your workup is saved — you can unlock anytime.",
      });
      // Clear the param so refresh doesn't re-toast.
      const next = new URLSearchParams(searchParams);
      next.delete("checkout");
      next.delete("session_id");
      setSearchParams(next, { replace: true });
    }
  }, [checkoutStatus, searchParams, setSearchParams]);

  useEffect(() => {
    if (checkoutStatus !== "success" || !sessionId) return;
    let cancelled = false;
    let elapsed = 0;
    const started = Date.now();
    setPolling(true);
    setPollTimedOut(false);

    async function tick() {
      if (cancelled) return;
      try {
        const { paid } = await verifyCheckout(sessionId);
        if (paid) {
          setPolling(false);
          toast.success("Payment successful", {
            description: "Your workup is unlocked and sent to your email.",
          });
          // Refetch the report so is_unlocked flips.
          try {
            const fresh = await getReport(reportId);
            if (!cancelled) setReport(fresh);
          } catch (_) { /* ignore */ }
          // Clean up query params.
          const next = new URLSearchParams(searchParams);
          next.delete("checkout");
          next.delete("session_id");
          setSearchParams(next, { replace: true });
          return;
        }
      } catch (_) { /* keep polling */ }
      elapsed = Date.now() - started;
      if (elapsed > 20_000) {
        if (!cancelled) {
          setPolling(false);
          setPollTimedOut(true);
        }
        return;
      }
      setTimeout(tick, 2000);
    }
    tick();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutStatus, sessionId]);

  if (loading) {
    return (
      <>
        <Header variant="app" />
        <LoadingReport />
      </>
    );
  }

  if (error || !report) {
    return (
      <>
        <Header variant="app" />
        <section className="max-w-2xl mx-auto px-6 py-24 text-center">
          <AlertCircle className="h-10 w-10 text-brand-blue mx-auto" strokeWidth={2} />
          <h1 className="mt-4 font-heading text-2xl font-semibold text-ink-900">
            {error || "Something went wrong"}
          </h1>
          <p className="mt-2 text-ink-600">The workup you&apos;re looking for doesn&apos;t exist or was removed.</p>
          <Link
            to="/"
            data-testid="report-not-found-home"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-blue px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-blue-hover transition-colors"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={2.4} /> Back to home
          </Link>
        </section>
        <Footer />
      </>
    );
  }

  const allTests = report.recommended_tests || [];
  const generatedDate = new Date(report.generated_at).toLocaleDateString(undefined, {
    year: "numeric", month: "long", day: "numeric",
  });

  // Filter tests based on active tab, then group by category.
  const activeGroups = FILTER_TABS.find((f) => f.key === filter)?.groups ?? ["labs", "imaging", "specialty"];
  const filteredTests = allTests.filter((t) => activeGroups.includes(groupOf(t.category)));
  const grouped = groupTestsByCategory(filteredTests);

  // For locked mode: preview first test (in original order); overlay+blur everything after it.
  // We compute against the ORIGINAL order so the first test is deterministic regardless of filter.
  const firstTestIndex = 0;
  const firstTest = allTests[firstTestIndex];
  const firstTestVisibleInFilter = firstTest && activeGroups.includes(groupOf(firstTest.category));

  const showImagingEmpty = filter === "imaging" && filteredTests.length === 0;

  return (
    <>
      <Header variant="app" />

      {/* Processing-payment overlay after Stripe checkout redirect */}
      {polling && (
        <div
          data-testid="checkout-processing-overlay"
          className="fixed inset-0 z-50 bg-white/80 backdrop-blur-sm flex items-center justify-center p-6"
        >
          <div className="rounded-3xl bg-white border border-slate-200 shadow-2xl px-8 py-10 text-center max-w-md w-full">
            <Loader2 className="h-10 w-10 mx-auto text-brand-blue animate-spin" strokeWidth={2} />
            <h2 className="mt-4 font-heading text-xl font-semibold text-ink-900">
              Processing your payment...
            </h2>
            <p className="mt-2 text-sm text-ink-600">
              We&apos;re confirming with Stripe. This usually takes a few seconds.
            </p>
          </div>
        </div>
      )}
      {pollTimedOut && (
        <div
          data-testid="checkout-timeout-banner"
          className="max-w-4xl mx-auto mt-6 mx-6 rounded-2xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3"
        >
          <AlertCircle className="h-5 w-5 text-amber-700 mt-0.5" strokeWidth={2.2} />
          <div className="flex-1">
            <p className="text-sm text-amber-900 font-medium">
              Payment received — your workup will unlock shortly.
            </p>
            <p className="text-xs text-amber-800 mt-1">Try refreshing in a minute.</p>
          </div>
          <Button
            data-testid="checkout-refresh-btn"
            variant="outline"
            onClick={() => window.location.reload()}
            className="rounded-full bg-white"
          >
            Refresh
          </Button>
        </div>
      )}

      <section className="max-w-4xl mx-auto px-6 py-10 md:py-14">
        {/* top meta */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link
            to="/"
            data-testid="report-back-home"
            className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-900 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={2.4} /> Back
          </Link>
          <div className="flex items-center gap-2">
            {unlocked ? (
              <span
                data-testid="report-unlocked-badge"
                className="inline-flex items-center gap-1 rounded-full bg-brand-green-light px-3 py-1 text-xs font-semibold text-brand-green-hover"
              >
                <Sparkles className="h-3.5 w-3.5" strokeWidth={2.4} /> Unlocked
              </span>
            ) : (
              <span
                data-testid="report-locked-badge"
                className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-ink-600"
              >
                <Lock className="h-3.5 w-3.5" strokeWidth={2.4} /> Teaser
              </span>
            )}
            <Button
              onClick={onShare}
              data-testid="report-share-btn"
              variant="outline"
              className="rounded-full border-slate-200 hover:bg-surface-subtle"
            >
              <Share2 className="h-4 w-4 mr-1.5" strokeWidth={2.2} /> Share
            </Button>
            {unlocked && (
              <Button
                onClick={onPrint}
                data-testid="report-print-btn"
                className="rounded-full bg-brand-green text-white hover:bg-brand-green-hover"
              >
                <Printer className="h-4 w-4 mr-1.5" strokeWidth={2.2} />
                Print for clinic visit
              </Button>
            )}
          </div>
        </div>

        {/* Visible shareable URL row */}
        <div
          data-testid="report-share-url-row"
          className="mt-4 flex items-stretch gap-2 rounded-2xl bg-white border border-slate-200 p-1.5 shadow-sm"
        >
          <Input
            readOnly
            value={reportUrl()}
            data-testid="report-share-url-input"
            aria-label="Shareable workup URL"
            onFocus={(e) => e.currentTarget.select()}
            onClick={(e) => e.currentTarget.select()}
            className="flex-1 border-0 bg-transparent text-sm text-ink-700 focus-visible:ring-0 focus-visible:ring-offset-0"
          />
          <Button
            onClick={onShare}
            data-testid="report-share-url-copy"
            className="rounded-full bg-brand-blue text-white hover:bg-brand-blue-hover px-4"
          >
            <Copy className="h-4 w-4 mr-1.5" strokeWidth={2.2} /> Copy
          </Button>
        </div>

        {/* Top disclaimer strip */}
        <div
          data-testid="report-top-disclaimer"
          className="mt-6 flex items-start gap-3 rounded-2xl bg-brand-blue-light/50 border border-brand-blue-light p-4"
        >
          <AlertCircle className="h-5 w-5 text-brand-blue shrink-0 mt-0.5" strokeWidth={2.2} />
          <p className="text-xs text-ink-700 leading-relaxed">{report.disclaimer}</p>
        </div>

        {/* Title + summary */}
        <header className="mt-8">
          <span className="text-xs font-semibold uppercase tracking-widest text-brand-blue">
            Your workup · Generated {generatedDate}
          </span>
          <h1
            data-testid="report-headline"
            className="mt-3 font-heading text-3xl md:text-4xl font-bold tracking-tight text-ink-900"
          >
            Your personalized diagnostic workup
          </h1>
          <p
            data-testid="report-summary"
            className="mt-3 text-lg text-ink-600 leading-relaxed max-w-3xl"
          >
            {report.summary}
          </p>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <span data-testid="report-test-count" className="text-sm text-ink-500">
              {allTests.length} tests recommended
            </span>
            <span
              data-testid="imaging-indicated-flag"
              data-value={report.imaging_indicated ? "true" : "false"}
              className={`inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border ${
                report.imaging_indicated
                  ? "bg-amber-50 text-amber-800 border-amber-200"
                  : "bg-slate-50 text-ink-500 border-slate-200"
              }`}
            >
              <ScanLine className="h-3 w-3" strokeWidth={2.4} />
              {report.imaging_indicated ? "Imaging indicated" : "No imaging indicated"}
            </span>
          </div>
        </header>

        {/* FM context */}
        <div
          data-testid="report-fm-context"
          className="mt-8 rounded-2xl bg-surface-subtle border border-slate-100 p-6"
        >
          <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-hover">
            Functional medicine context
          </span>
          <p className="mt-2 text-ink-700 leading-relaxed">{report.functional_medicine_context}</p>
        </div>

        {/* Filter tabs */}
        <div className="mt-10">
          <div
            data-testid="filter-tabs"
            role="tablist"
            className="inline-flex items-center gap-1 rounded-full bg-slate-100 p-1"
          >
            {FILTER_TABS.map((tab) => {
              const active = filter === tab.key;
              return (
                <button
                  key={tab.key}
                  role="tab"
                  aria-selected={active}
                  data-testid={`filter-tab-${tab.key}`}
                  onClick={() => setFilter(tab.key)}
                  className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-all ${
                    active
                      ? "bg-white text-ink-900 shadow-sm"
                      : "text-ink-500 hover:text-ink-900"
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tests area */}
        <div className="mt-6 relative">
          {showImagingEmpty ? (
            <div
              data-testid="imaging-empty-state"
              className="rounded-2xl bg-white border border-slate-100 p-8 text-center"
            >
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-green-light text-brand-green-hover">
                <Info className="h-6 w-6" strokeWidth={2.2} />
              </span>
              <h3 className="mt-4 font-heading text-lg font-semibold text-ink-900">
                No imaging is indicated based on your symptoms.
              </h3>
              <p className="mt-2 text-sm text-ink-600 max-w-lg mx-auto leading-relaxed">
                Functional medicine best practice is to start with lab work and only escalate to imaging when specific clinical red flags are present.
              </p>
            </div>
          ) : (
            <>
              {/* If locked and the first test isn't in the current filter, show a friendly
                  note; otherwise render the first test un-overlaid so the tease is visible. */}
              {!unlocked && !firstTestVisibleInFilter && grouped.length > 0 && (
                <div
                  data-testid="filter-tease-note"
                  className="mb-4 rounded-xl bg-brand-blue-light/40 border border-brand-blue-light p-4 text-sm text-ink-700"
                >
                  Preview available under the <b>All</b> tab. Unlock to see this category in detail.
                </div>
              )}

              {/* First-test preview (only when locked, filter shows it, and it exists) */}
              {!unlocked && firstTestVisibleInFilter && (
                <TestCard
                  test={firstTest}
                  index={0}
                  blurBody={true}
                  isImaging={firstTest.category === "imaging"}
                />
              )}

              {/* Rest of tests, grouped by category. Blur when locked. */}
              <div className={!unlocked ? "relative mt-4" : "mt-0"}>
                {/* Locked mode: render blurred grouped tests + overlay */}
                {!unlocked && (
                  <div
                    style={{ filter: "blur(6px)", pointerEvents: "none", userSelect: "none" }}
                    aria-hidden={true}
                  >
                    {grouped.map((group) => {
                      const testsForGroup =
                        firstTestVisibleInFilter && group.category === firstTest.category
                          ? group.tests.filter((t) => t !== firstTest)
                          : group.tests;
                      if (testsForGroup.length === 0) return null;
                      return (
                        <CategoryGroup
                          key={group.category}
                          group={{ ...group, tests: testsForGroup }}
                          blurAll={false}
                          firstTestIndex={-1}
                          allTests={allTests}
                        />
                      );
                    })}
                  </div>
                )}

                {/* unlock overlay */}
                {!unlocked && (grouped.length > 0 || filteredTests.length > 1) && (
                  <div
                    data-testid="unlock-overlay"
                    className="absolute inset-0 flex items-center justify-center p-4"
                  >
                    <div className="w-full max-w-md rounded-3xl bg-white/95 backdrop-blur border border-slate-200 shadow-2xl p-8 text-center">
                      <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-blue-light text-brand-blue">
                        <Lock className="h-6 w-6" strokeWidth={2.2} />
                      </span>
                      <h3 className="mt-4 font-heading text-xl font-semibold text-ink-900">
                        Unlock your full workup
                      </h3>
                      <p className="mt-2 text-sm text-ink-600 leading-relaxed">
                        See all {allTests.length} tests, why each one matters for you, and what abnormal results can reveal.
                      </p>

                      <div className="mt-6 grid gap-3">
                        <button
                          onClick={() => onUnlockCta("onetime")}
                          data-testid="unlock-onetime-btn"
                          className="w-full rounded-full bg-brand-blue px-5 py-3 text-sm font-semibold text-white hover:bg-brand-blue-hover transition-transform hover:-translate-y-0.5 active:scale-95"
                        >
                          Unlock full workup — $9 one-time
                        </button>
                        <button
                          onClick={() => onUnlockCta("subscription")}
                          data-testid="unlock-subscription-btn"
                          className="w-full rounded-full bg-ink-900 px-5 py-3 text-sm font-semibold text-white hover:bg-ink-700 transition-transform hover:-translate-y-0.5 active:scale-95"
                        >
                          Go unlimited — $19/month
                        </button>
                      </div>
                      <p className="mt-4 text-[11px] text-ink-500">Secure checkout coming soon.</p>
                    </div>
                  </div>
                )}

                {/* Unlocked: render grouped tests normally */}
                {unlocked &&
                  grouped.map((group) => (
                    <CategoryGroup
                      key={group.category}
                      group={group}
                      blurAll={false}
                      firstTestIndex={-1}
                      allTests={allTests}
                    />
                  ))}
              </div>
            </>
          )}
        </div>

        {/* Next steps */}
        <div data-testid="report-next-steps" className="mt-12 rounded-2xl bg-white border border-slate-100 p-6 shadow-sm">
          <h2 className="font-heading text-xl font-semibold text-ink-900">Next steps</h2>
          <NextSteps text={report.next_steps} unlocked={unlocked} />
        </div>

        {/* Bottom disclaimer */}
        <div className="mt-10 rounded-2xl bg-surface-subtle border border-slate-100 p-6">
          <p className="text-xs text-ink-500 leading-relaxed">{report.disclaimer}</p>
        </div>
      </section>

      {/* Email prompt modal for Stripe checkout */}
      <Dialog
        open={modalPlan !== null}
        onOpenChange={(o) => !o && !starting && setModalPlan(null)}
      >
        <DialogContent data-testid="checkout-email-modal" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {modalPlan === "subscription"
                ? "Go unlimited — $19/month"
                : "Unlock your full workup — $9"}
            </DialogTitle>
            <DialogDescription>
              We&apos;ll send your workup to this email so you can bring it to your practitioner.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmitCheckout} className="space-y-3">
            <div className="relative">
              <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" strokeWidth={2.2} />
              <Input
                type="email"
                autoFocus
                required
                data-testid="checkout-email-input"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
                className="pl-9 rounded-xl"
              />
            </div>
            {emailError && (
              <p data-testid="checkout-email-error" className="text-xs text-red-600">
                {emailError}
              </p>
            )}
            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                disabled={starting}
                onClick={() => setModalPlan(null)}
                data-testid="checkout-modal-cancel"
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={starting}
                data-testid="checkout-modal-continue"
                className="rounded-full bg-brand-blue text-white hover:bg-brand-blue-hover"
              >
                {starting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Starting...
                  </>
                ) : (
                  <>Continue to secure checkout</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Footer />
    </>
  );
}

function NextSteps({ text, unlocked }) {
  if (!text) return null;
  if (unlocked) {
    return <p className="mt-2 text-ink-700 leading-relaxed">{text}</p>;
  }
  const match = text.match(/^(.*?[.!?])\s+([\s\S]+)$/);
  const first = match ? match[1] : text;
  const rest = match ? match[2] : "";
  return (
    <p className="mt-2 text-ink-700 leading-relaxed">
      {first}{" "}
      {rest && (
        <span
          style={{ filter: "blur(6px)", userSelect: "none" }}
          aria-hidden="true"
        >
          {rest}
        </span>
      )}
    </p>
  );
}
